import React, { useEffect, useState } from 'react';
import TaskFormFields from '../../components/TaskFormFields';
import TaskCalendarView from '../../components/TaskCalendarView';
import RegularizationApprovals from '../../components/RegularizationApprovals';
import { apiGet, apiPost, apiPut, apiPatch, apiDel } from '../../lib/api';
import { CheckCircle2, Circle, Plus, Trash2, X, AlertCircle, Calendar, Clock, Link2, LayoutGrid, CalendarDays } from 'lucide-react';
import { toast } from 'sonner';
import LeadDetailDrawer from '../../components/crm/LeadDetailDrawer';
import { useAuth } from '../../contexts/AuthContext';
import PendingDprCard from '../../components/dpr/PendingDprCard';

const FILTERS = [
  { key: 'overdue', label: 'Overdue', color: 'bg-red-100 text-red-700' },
  { key: 'due_today', label: 'Due Today', color: 'bg-amber-100 text-amber-700' },
  { key: 'this_week', label: 'This Week', color: 'bg-blue-100 text-blue-700' },
  { key: 'upcoming', label: 'Upcoming', color: 'bg-slate-100 text-slate-700' },
  { key: 'all', label: 'All Open', color: 'bg-indigo-50 text-[#3b4872]' },
];

import { formatDateTime as fmtDateTime } from '../../lib/format';

const EMPTY = { description: '', due_at: '', owner: '', assigned_to: '', linked_entity: null };

export default function MyTasks() {
  const { user } = useAuth() || {};
  const [data, setData] = useState({ overdue: [], due_today: [], this_week: [], upcoming: [], counts: {} });
  const [activeFilter, setActiveFilter] = useState('overdue');
  const [showForm, setShowForm] = useState(false);
  const [openDrawer, setOpenDrawer] = useState(null); // {entity_type, entity_id}
  const [form, setForm] = useState({ description: '', reminder_date: '', owner: '', entity_type: '', entity_id: '' });
  const [scope, setScope] = useState('mine'); // 'mine' | 'team' | 'all'
  const [viewMode, setViewMode] = useState('buckets'); // 'buckets' | 'calendar'
  // Privileged extra filters (MD / super_admin only)
  const [filterDept, setFilterDept] = useState('');     // '' = all departments
  const [filterEmp, setFilterEmp] = useState('');       // '' = all employees
  const [employees, setEmployees] = useState([]);       // {id, name, department}

  const isPrivileged = user?.role === 'super_admin' || user?.department === 'MD';
  const myName = user?.name || user?.email;
  const myDept = user?.department || '';

  // My-team = colleagues in same department. Privileged see ALL employees.
  const teamMemberNames = React.useMemo(() => {
    const list = isPrivileged
      ? employees
      : employees.filter((e) => (e.department || '').toLowerCase() === myDept.toLowerCase());
    return new Set(list.map((e) => (e.name || '').toLowerCase()).filter(Boolean));
  }, [employees, myDept, isPrivileged]);

  const departments = React.useMemo(() => {
    return Array.from(new Set(employees.map((e) => e.department).filter(Boolean))).sort();
  }, [employees]);

  // Categorize a task into bucket (overdue / due_today / this_week / upcoming) by due date
  const bucketOf = (dueIso) => {
    if (!dueIso) return 'upcoming';
    const due = new Date(dueIso);
    if (Number.isNaN(due.getTime())) return 'upcoming';
    const today = new Date(); today.setHours(0, 0, 0, 0);
    const tomorrow = new Date(today); tomorrow.setDate(tomorrow.getDate() + 1);
    const weekEnd = new Date(today); weekEnd.setDate(weekEnd.getDate() + 7);
    if (due < today) return 'overdue';
    if (due < tomorrow) return 'due_today';
    if (due < weekEnd) return 'this_week';
    return 'upcoming';
  };

  // Map a Kanban task → unified shape compatible with the existing render code
  const kanbanToUnified = (kt) => ({
    id: kt.id,
    description: kt.title || kt.description || '(untitled)',
    reminder_date: kt.due_date || null,
    owner: kt.owner || kt.created_by || '',
    assigned_to: kt.assigned_to || kt.assignee || '',
    created_by: kt.created_by || '',   // ← carry this so isMine condition 3 works
    entity_type: kt.linked_entity_type || (kt.project_id ? 'project' : null),
    entity_id: kt.linked_entity_id || kt.project_id || null,
    entity_label: kt.linked_entity_label || null,
    linked_entity_type: kt.linked_entity_type,
    linked_entity_label: kt.linked_entity_label,
    source: 'kanban',
    status: kt.status,
    priority: kt.priority,
  });

  const isMine = (t) => {
    if (!myName) return true;
    const n = myName.toLowerCase();
    const myEmail = (user?.email || '').toLowerCase();
    const owner = (t.owner || '').toLowerCase();
    const assigned = (t.assigned_to || '').toLowerCase();
    const createdBy = (t.created_by || '').toLowerCase();
    const firstNameMatch = n.length > 3 && (
      owner.startsWith(n.split(' ')[0].toLowerCase()) ||
      assigned.startsWith(n.split(' ')[0].toLowerCase())
    );
    return owner === n
        || owner === myEmail
        || assigned === n
        || assigned === myEmail
        || createdBy === myEmail
        || createdBy === n
        || firstNameMatch;
  };

  const isInMyTeam = (t) => {
    const o = (t.owner || '').toLowerCase();
    const a = (t.assigned_to || '').toLowerCase();
    return (o && teamMemberNames.has(o)) || (a && teamMemberNames.has(a));
  };

  const matchesEmployeeFilter = (t) => {
    if (!filterEmp) return true;
    const n = filterEmp.toLowerCase();
    return (t.owner || '').toLowerCase() === n || (t.assigned_to || '').toLowerCase() === n;
  };

  const matchesDeptFilter = (t) => {
    if (!filterDept) return true;
    // Look up the dept of owner/assigned_to via employees map
    const empByName = new Map(employees.map((e) => [(e.name || '').toLowerCase(), e.department]));
    const oDept = empByName.get((t.owner || '').toLowerCase()) || '';
    const aDept = empByName.get((t.assigned_to || '').toLowerCase()) || '';
    return oDept === filterDept || aDept === filterDept;
  };

  const load = async () => {
    try {
      // 1. CRM reminders (bucketed). Iter 113.3 — pass scope so the API returns
      //    just the caller's tasks when scope='mine', else everyone's.
      const crmScope = scope === 'mine' ? 'mine' : 'all';
      const crm = await apiGet(`/crm/tasks/my?scope=${crmScope}`).catch(() => null);
      // 2. Kanban tasks
      const kb = await apiGet('/task-board').catch(() => null);
      const kanbanTasks = [];
      if (kb?.columns) {
        for (const status of Object.keys(kb.columns)) {
          if (status === 'Done') continue; // skip completed
          for (const kt of kb.columns[status]) kanbanTasks.push(kanbanToUnified(kt));
        }
      }

      // Merge — start from CRM buckets, then add Kanban into the right bucket
      const buckets = {
        overdue: [...(crm?.overdue || [])],
        due_today: [...(crm?.due_today || [])],
        this_week: [...(crm?.this_week || [])],
        upcoming: [...(crm?.upcoming || [])],
      };
      for (const t of kanbanTasks) buckets[bucketOf(t.reminder_date)].push(t);

      // Build the predicate based on scope + extra filters
      const scopePred = (t) => {
        if (scope === 'mine') return isMine(t);
        if (scope === 'team') return isInMyTeam(t) || isMine(t);
        // 'all' — visible only to privileged; non-privileged falls back to mine
        return isPrivileged ? true : isMine(t);
      };
      const finalPred = (t) =>
        scopePred(t) && matchesDeptFilter(t) && matchesEmployeeFilter(t);

      const apply = Object.fromEntries(
        Object.entries(buckets).map(([k, arr]) => [k, arr.filter(finalPred)])
      );

      apply.counts = {
        overdue: apply.overdue.length,
        due_today: apply.due_today.length,
        this_week: apply.this_week.length,
        upcoming: apply.upcoming.length,
        total_open: apply.overdue.length + apply.due_today.length + apply.this_week.length + apply.upcoming.length,
      };
      setData(apply);
    } catch (err) {
      console.error('Failed to load tasks:', err);
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [scope, filterDept, filterEmp, user?.email]);

  // Load employee list once — uses /employees/lookup (any authenticated user, Batch H §6 fix)
  useEffect(() => {
    apiGet('/employees/lookup', { limit: 200 })
      .then((r) => setEmployees((r.items || []).map((e) => ({ id: e.id, name: e.name, department: e.department }))))
      .catch(() => {});
  }, []);

  const visibleTasks = activeFilter === 'all'
    ? [...data.overdue, ...data.due_today, ...data.this_week, ...data.upcoming]
    : data[activeFilter] || [];

  const handleComplete = async (task) => {
    try {
      if (task.source === 'kanban') {
        await apiPut(`/tasks/${task.id}/move`, { status: 'Done' });
      } else {
        await apiPut(`/crm/tasks/${task.id}/complete`);
      }
      toast.success('Task completed');
      load();
    } catch (err) {
      toast.error('Failed to complete task');
    }
  };

  const handleDelete = async (task) => {
    if (!window.confirm('Delete this task?\n\nThis action cannot be undone.')) return;
    try {
      if (task.source === 'kanban') {
        await apiDel(`/tasks/${task.id}`);
      } else {
        await apiDel(`/crm/reminders/${task.id}`);
      }
      toast.success('Task deleted');
      load();
    } catch (err) {
      toast.error('Failed to delete task');
    }
  };

  const handleReschedule = async (task, newIso) => {
    try {
      if (task.source === 'kanban') {
        await apiPut(`/tasks/${task.id}`, { due_date: newIso });
      } else {
        await apiPatch(`/crm/reminders/${task.id}/reschedule`, { reminder_date: newIso });
      }
      const when = fmtDateTime(newIso);
      toast.success(`Rescheduled to ${when}`);
      load();
    } catch (err) {
      toast.error('Failed to reschedule task');
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.description.trim() || !form.due_at) {
      toast.error('Description and due date are required');
      return;
    }
    try {
      const payload = {
        description: form.description.trim(),
        reminder_date: new Date(form.due_at).toISOString(),
        owner: form.owner || undefined,
        assigned_to: form.assigned_to || undefined,
        is_completed: false,
      };
      if (form.linked_entity) {
        payload.linked_entity_type = form.linked_entity.type;
        payload.linked_entity_id = form.linked_entity.id;
        payload.linked_entity_label = form.linked_entity.label;
        // Also set legacy entity_type/id so existing drawer-open works for lead/opportunity/project
        if (['lead', 'opportunity', 'project'].includes(form.linked_entity.type)) {
          payload.entity_type = form.linked_entity.type;
          payload.entity_id = form.linked_entity.id;
        }
      }
      await apiPost('/crm/reminders', payload);
      toast.success('Task created');
      setShowForm(false);
      setForm(EMPTY);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create task');
    }
  };

  const openParent = (task) => {
    if (task.entity_type && task.entity_id) {
      setOpenDrawer({ entity_type: task.entity_type, entity_id: task.entity_id });
    }
  };

  return (
    <div className="px-3 md:px-0 pt-3 md:pt-0">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
        <div>
          <div className="text-[10px] font-display font-bold uppercase tracking-[0.2em] text-[#3b4872] mb-2">Overview</div>
          <h1 className="font-display text-2xl md:text-3xl font-extrabold tracking-tight">My Tasks</h1>
          <p className="text-xs md:text-sm text-slate-600 mt-1">
            Unified view — CRM follow-ups + Project task board, all in one place
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {/* Scope toggle: Mine / My Team / (privileged) All Team */}
          <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden" data-testid="scope-toggle">
            <button
              onClick={() => setScope('mine')}
              className={`px-3 py-2 text-xs font-display font-semibold uppercase tracking-wider ${scope === 'mine' ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="scope-mine"
            >
              Mine
            </button>
            <button
              onClick={() => setScope('team')}
              className={`px-3 py-2 text-xs font-display font-semibold uppercase tracking-wider border-l border-slate-300 ${scope === 'team' ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="scope-team"
              title={isPrivileged ? 'All employees (you head multiple departments)' : `Your department: ${myDept || '—'}`}
            >
              My Team
            </button>
            {isPrivileged && (
              <button
                onClick={() => setScope('all')}
                className={`px-3 py-2 text-xs font-display font-semibold uppercase tracking-wider border-l border-slate-300 ${scope === 'all' ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
                data-testid="scope-all"
              >
                All Team
              </button>
            )}
          </div>
          {/* View toggle: Buckets ↔ Calendar */}
          <div className="inline-flex border border-slate-300 rounded-sm overflow-hidden" data-testid="view-toggle">
            <button
              onClick={() => setViewMode('buckets')}
              className={`px-3 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-1.5 ${viewMode === 'buckets' ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="view-buckets"
              title="Bucket view"
            >
              <LayoutGrid size={13} /> Buckets
            </button>
            <button
              onClick={() => setViewMode('calendar')}
              className={`px-3 py-2 text-xs font-display font-semibold uppercase tracking-wider flex items-center gap-1.5 border-l border-slate-300 ${viewMode === 'calendar' ? 'bg-[#3b4872] text-white' : 'bg-white text-slate-700 hover:bg-slate-50'}`}
              data-testid="view-calendar"
              title="Calendar view"
            >
              <CalendarDays size={13} /> Calendar
            </button>
          </div>
          <button onClick={() => setShowForm(true)} className="btn-primary flex items-center gap-2" data-testid="add-task-btn">
            <Plus size={16} /> Add Task
          </button>
        </div>
      </div>

      {/* Item 10 rebuild — which DPR sections haven't been filled today */}
      <PendingDprCard />

      {/* Approver-only: pending attendance regularization requests */}
      <RegularizationApprovals />

      {/* Privileged filters: Department + Employee (MD / super_admin who head multiple depts) */}
      {isPrivileged && (
        <div className="flex flex-wrap items-center gap-2 mb-4 -mt-2" data-testid="privileged-filters">
          <span className="text-[10px] font-display font-bold uppercase tracking-[0.18em] text-slate-500">Filter</span>
          <select
            value={filterDept}
            onChange={(e) => setFilterDept(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white focus:border-[#3b4872] focus:outline-none"
            data-testid="filter-department"
          >
            <option value="">All departments</option>
            {departments.map((d) => <option key={d} value={d}>{d}</option>)}
          </select>
          <select
            value={filterEmp}
            onChange={(e) => setFilterEmp(e.target.value)}
            className="px-3 py-1.5 border border-slate-300 rounded-sm text-xs bg-white focus:border-[#3b4872] focus:outline-none min-w-[200px]"
            data-testid="filter-employee"
          >
            <option value="">All employees</option>
            {employees
              .filter((e) => !filterDept || e.department === filterDept)
              .map((e) => <option key={e.id || e.name} value={e.name}>{e.name}{e.department ? ` · ${e.department}` : ''}</option>)}
          </select>
          {(filterDept || filterEmp) && (
            <button
              onClick={() => { setFilterDept(''); setFilterEmp(''); }}
              className="text-[11px] font-display font-semibold uppercase tracking-wider text-[#3b4872] hover:underline"
              data-testid="filter-clear"
            >
              Clear filters
            </button>
          )}
        </div>
      )}

      {viewMode === 'calendar' ? (
        <TaskCalendarView
          tasks={[...(data.overdue || []), ...(data.due_today || []), ...(data.this_week || []), ...(data.upcoming || [])]}
          onComplete={handleComplete}
          onDelete={handleDelete}
          onOpenParent={openParent}
          onReschedule={handleReschedule}
        />
      ) : (
        <>
      {/* Filter Tabs / Bucket counts */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-5">
        {FILTERS.map(f => {
          const count = f.key === 'all'
            ? (data.counts?.total_open || 0)
            : (data.counts?.[f.key] || 0);
          const isActive = activeFilter === f.key;
          return (
            <button
              key={f.key}
              onClick={() => setActiveFilter(f.key)}
              className={`border rounded-sm p-4 text-left transition-all ${
                isActive ? 'border-[#3b4872] bg-white shadow-sm' : 'border-slate-200 bg-white hover:border-slate-300'
              }`}
              data-testid={`filter-${f.key}`}
            >
              <div className={`inline-block text-[10px] font-display font-bold uppercase tracking-[0.18em] px-2 py-0.5 rounded-sm mb-2 ${f.color}`}>
                {f.label}
              </div>
              <div className="font-display text-3xl font-extrabold text-[#0F172A]">{count}</div>
            </button>
          );
        })}
      </div>

      {/* Task list */}
      <div className="bg-white border border-slate-200 rounded-sm" data-testid="task-list">
        {visibleTasks.length === 0 ? (
          <div className="p-12 text-center">
            <CheckCircle2 size={32} className="text-emerald-500 mx-auto mb-2" />
            <p className="text-sm text-slate-500">Nothing in this bucket. You're all caught up.</p>
          </div>
        ) : (
          <ul className="divide-y divide-slate-100">
            {visibleTasks.map(t => {
              const overdue = (() => {
                if (!t.reminder_date) return false;
                return t.reminder_date.slice(0, 10) < new Date().toISOString().slice(0, 10);
              })();
              return (
                <li key={t.id} className="px-4 py-3 hover:bg-slate-50 transition-colors flex items-center gap-3" data-testid={`task-row-${t.id}`}>
                  <button onClick={() => handleComplete(t)} className="text-slate-400 hover:text-[#3b4872]" data-testid={`task-complete-${t.id}`}>
                    <Circle size={18} />
                  </button>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-display font-semibold text-sm text-[#0F172A]">{t.description}</span>
                      {overdue && (
                        <span className="inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] uppercase tracking-wider bg-red-100 text-red-700 rounded-sm">
                          <AlertCircle size={10} /> Overdue
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-slate-500 mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-0.5">
                      <span className="inline-flex items-center gap-1"><Calendar size={11} /> {fmtDateTime(t.reminder_date)}</span>
                      {t.owner && <span className="inline-flex items-center gap-1" title="Owner / creator"><Clock size={11} /> {t.owner}</span>}
                      {t.assigned_to && (
                        <span className="inline-flex items-center gap-1 text-[#3b4872]" title="Assigned to">
                          → {t.assigned_to}
                        </span>
                      )}
                      {(t.linked_entity_label || t.entity_label) && (
                        <button onClick={() => openParent(t)} className="inline-flex items-center gap-1 text-[#3b4872] hover:underline font-semibold" data-testid={`task-parent-${t.id}`}>
                          <Link2 size={11} /> {(t.linked_entity_type || t.entity_type)}: {t.linked_entity_label || t.entity_label}
                        </button>
                      )}
                    </div>
                  </div>
                  <button onClick={() => handleDelete(t)} className="text-red-600 hover:text-red-700"><Trash2 size={14} /></button>
                </li>
              );
            })}
          </ul>
        )}
      </div>
      </>
      )}

      {openDrawer && (
        <LeadDetailDrawer
          entityType={openDrawer.entity_type}
          entityId={openDrawer.entity_id}
          onClose={() => { setOpenDrawer(null); }}
          onUpdated={load}
        />
      )}

      {showForm && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-sm shadow-xl max-w-xl w-full max-h-[90vh] overflow-y-auto">
            <div className="border-b border-slate-200 p-5 flex items-center justify-between">
              <h2 className="font-display text-xl font-bold">Add Task</h2>
              <button onClick={() => setShowForm(false)} className="text-slate-500 hover:text-slate-700"><X size={20} /></button>
            </div>
            <form onSubmit={handleCreate} className="p-5 space-y-4">
              <TaskFormFields value={form} onChange={setForm} testIdPrefix="task" />
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">Cancel</button>
                <button type="submit" className="btn-primary" data-testid="task-submit-btn">Create Task</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
